import React, { useState } from "react";
import { Container, Grid, Typography, Box, TextField, Alert, CircularProgress, keyframes } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import { Section, SectionLabel, PillButton, DP, GradientText, fadeInUp, slideInRight } from "./HomeStyles";
import { apiClient } from "../../services/authService";
import { useLanguage } from "../../contexts/LanguageContext";

const borderRotate = keyframes`
  from { transform: translate(-50%, -50%) rotate(0deg); }
  to   { transform: translate(-50%, -50%) rotate(360deg); }
`;

const cardWrapperSx = {
  position: "relative",
  borderRadius: "26px",
  overflow: "hidden",
  padding: "2px",
  "&::before": {
    content: '""',
    position: "absolute",
    top: "50%",
    left: "50%",
    width: "200%",
    height: "200%",
    background: `conic-gradient(from 0deg, ${DP.blue} 0deg, ${DP.blueLight} 90deg, ${DP.bluePale} 180deg, ${DP.blueLight} 270deg, ${DP.blue} 360deg)`,
    animation: `${borderRotate} 4s linear infinite`,
    zIndex: 0,
  },
};

const cardInnerSx = {
  position: "relative" as const,
  zIndex: 1,
  borderRadius: "24px",
  background: "rgba(255,255,255,0.97)",
  backdropFilter: "blur(20px)",
  height: "100%",
};

const inputSx = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: DP.white,
    borderRadius: "12px",
    color: DP.textDark,
    "& fieldset": { borderColor: DP.border },
    "&:hover fieldset": { borderColor: DP.blueMid },
    "&.Mui-focused fieldset": { borderColor: DP.blue, borderWidth: "2px" },
  },
  "& .MuiInputLabel-root": { color: DP.textMuted },
  "& .MuiInputLabel-root.Mui-focused": { color: DP.blue },
  "& .MuiInputBase-input": { color: DP.textDark },
  "& .MuiInputBase-inputMultiline": { color: DP.textDark },
  "& .MuiFormHelperText-root": { color: DP.textMuted },
};

const ContactUs: React.FC = () => {
  const { t } = useLanguage();
  const tc = t.home.contact;
  const [form, setForm]       = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError]     = useState("");

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    if (error) setError("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    const { name, email, phone, subject, message } = form;
    if (!name.trim() || !email.trim() || !subject.trim() || !message.trim()) { setError(tc.errorFill); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setError(tc.errorEmail); return; }
    if (message.trim().length < 10) { setError(tc.errorMsg); return; }
    setLoading(true);
    try {
      await apiClient.post("/contact", { name: name.trim(), email: email.trim(), phone: phone.trim() || undefined, subject: subject.trim(), message: message.trim() });
      setSuccess(true);
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err: any) {
      setError(err?.response?.data?.message || tc.errorFill);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Section id="contact" sx={{ background: `linear-gradient(160deg, ${DP.offWhite} 0%, ${DP.blueBg} 60%, ${DP.blueBgDeep} 100%)`, "& .animate-in": { animation: `${fadeInUp} 1s ease-out both` } }}>
      <Container maxWidth="lg">
        {/* Heading */}
        <Box sx={{ textAlign: "center", mb: { xs: 6, md: 8 }, animation: `${fadeInUp} 0.9s ease-out both` }}>
          <SectionLabel sx={{ mb: 2 }}>
            <MailOutlineIcon sx={{ fontSize: "0.9rem" }} />
            {tc.sectionLabel}
          </SectionLabel>
          <Typography variant="h2" sx={{ mt: 2, fontSize: { xs: "2.2rem", md: "3.2rem" }, fontWeight: 800, lineHeight: 1.2, color: DP.textDark }}>
            {tc.heading} <GradientText>{tc.headingHighlight}</GradientText>
          </Typography>
          <Typography variant="body1" sx={{ mt: 2, fontSize: "1.05rem", color: DP.textMid, maxWidth: 520, mx: "auto", lineHeight: 1.8 }}>
            {tc.subtitle}
          </Typography>
        </Box>

        <Grid container justifyContent="center">
          {/* Form */}
          <Grid size={{ xs: 12, md: 8 }} sx={{ animation: `${slideInRight} 1s ease-out both`, animationDelay: "0.3s" }}>
            <Box sx={cardWrapperSx}>
              <Box component="form" onSubmit={handleSubmit} noValidate sx={{ ...cardInnerSx, p: { xs: 3, md: 4 } }}>
                {success ? (
                  <Box sx={{ textAlign: "center", py: 5 }}>
                    <CheckCircleOutlineIcon sx={{ fontSize: 72, color: DP.blue, mb: 2 }} />
                    <Typography variant="h5" sx={{ fontWeight: 700, color: DP.textDark, mb: 1 }}>{tc.successTitle}</Typography>
                    <Typography sx={{ color: DP.textMid, lineHeight: 1.7 }}>{tc.successBody}</Typography>
                    <PillButton variant="outlined" sx={{ mt: 3, borderColor: DP.blue, color: DP.blue, borderWidth: "2px", "&:hover": { backgroundColor: DP.blueBg } }} onClick={() => setSuccess(false)}>
                      {tc.sendAnother}
                    </PillButton>
                  </Box>
                ) : (
                  <>
                    {error && (
                      <Alert severity="error" sx={{ mb: 3, borderRadius: "12px", backgroundColor: "rgba(239,68,68,0.08)", color: "#B71C1C", "& .MuiAlert-icon": { color: "#C62828" } }}>
                        {error}
                      </Alert>
                    )}
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField label={tc.fields.name}    name="name"    value={form.name}    onChange={handleChange} fullWidth required sx={inputSx} />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField label={tc.fields.email}   name="email"   value={form.email}   onChange={handleChange} fullWidth required type="email" sx={inputSx} />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField label={tc.fields.phone}   name="phone"   value={form.phone}   onChange={handleChange} fullWidth type="tel" sx={inputSx} />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField label={tc.fields.subject} name="subject" value={form.subject} onChange={handleChange} fullWidth required sx={inputSx} />
                      </Grid>
                      <Grid size={{ xs: 12 }}>
                        <TextField label={tc.fields.message} name="message" value={form.message} onChange={handleChange} fullWidth required multiline rows={6} sx={inputSx} inputProps={{ maxLength: 5000 }} helperText={`${form.message.length}/5000`} />
                      </Grid>
                    </Grid>
                    <Box sx={{ mt: 3 }}>
                      <PillButton type="submit" variant="contained" size="large" disabled={loading} sx={{ backgroundColor: DP.blue, color: "#FFFFFF", px: 6, py: 1.8, fontSize: "1rem", fontWeight: 700, width: { xs: "100%", sm: "auto" }, boxShadow: `0 4px 24px ${DP.shadow}`, "&:hover": { backgroundColor: DP.navy }, "&:disabled": { opacity: 0.6 } }}>
                        {loading ? <CircularProgress size={22} color="inherit" /> : tc.submit}
                      </PillButton>
                    </Box>
                  </>
                )}
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Section>
  );
};

export default ContactUs;
